import {
  FileChooser,
  SearchDropdown,
  TextBox,
} from "../../../components/inputs";
import { useFormikWrapper } from "../../../components/formik_wrapper";
import * as Yup from "yup";
import { useEffect } from "react";
import { coworkerRoles } from "../../../util/master-data";

export function CoworkerForm({
  formik,
  readOnly,
  mode = "Create",
  showProfile = true,
  className = "",
}) {
  const user = JSON.parse(localStorage.getItem("user"));

  const self =
    mode === "Edit" && formik.values?.coworker_uid === user?.coworker_uid;
  return (
    <div
      className={`${className} text-left grid mt-4 mb-7 grid-cols-1 gap-x-12 gap-y-6 sm:grid-cols-2`}
    >
      <TextBox
        name="coworker_code"
        label="Coworker Code"
        placeholder="Coworker Code"
        formik={formik}
        readOnly={readOnly}
      />
      {!self && (
        <SearchDropdown
          name="role"
          label="Role"
          placeholder="Role"
          options={coworkerRoles}
          formik={formik}
          readOnly={readOnly}
        />
      )}
      <TextBox
        name="first_name"
        label="First Name"
        placeholder="First Name"
        formik={formik}
        readOnly={readOnly}
      />
      <TextBox
        name="last_name"
        label="Last Name"
        placeholder="Last Name"
        formik={formik}
        readOnly={readOnly}
      />
      <TextBox
        name="email"
        label="Email"
        placeholder="Email"
        formik={formik}
        readOnly={readOnly}
      />
      {mode === "Create" && (
        <TextBox
          name="password"
          label="Password"
          placeholder="Password"
          type="password"
          formik={formik}
          readOnly={readOnly}
        />
      )}
      <TextBox
        name="designation"
        label="Designation"
        placeholder="Designation"
        formik={formik}
        readOnly={readOnly}
      />
      <TextBox
        name="primary_phone_number"
        label="Primary Phone Number"
        placeholder="Primary Phone Number"
        formik={formik}
        readOnly={readOnly}
        type="tel"
      />
      <TextBox
        name="secondary_phone_number"
        label="Secondary Phone Number"
        placeholder="Secondary Phone Number"
        formik={formik}
        readOnly={readOnly}
        type="tel"
      />
      {showProfile && (
        <FileChooser
          formik={formik}
          readOnly={readOnly}
          name="profile_pic"
          label="Profile Picture"
          placeholder="Profile Picture"
          accept=".png,.jpg,.jpeg"
        />
      )}
    </div>
  );
}

const validationSchema = {
  edit: Yup.object().shape({
    coworker_code: Yup.string()
      .required("Coworker code is required")
      .min(3, "Minimum 3 characters required")
      .max(10, "Cannot exceed 10 chars"),
    email: Yup.string()
      .email("Please enter a valid email")
      .required("Email is required")
      .max(255, "Cannot exceed 255 chars"),
    first_name: Yup.string()
      .required("First name is required")
      .min(3, "Minimum 3 characters required")
      .max(255, "Cannot exceed 255 chars"),
    last_name: Yup.string().nullable().max(255, "Cannot exceed 255 chars"),
    designation: Yup.string()
      .nullable()
      .min(3, "Minimum 3 characters required")
      .max(50, "Cannot exceed 50 chars"),
    primary_phone_number: Yup.string('Please enter a valid phone number')
      .required('Primary phone number is required')
      .min(3, 'Please enter a valid phone number'),
    secondary_phone_number: Yup.string('Please enter a valid phone number')
      .nullable(),
    profile_pic: Yup.mixed().nullable(),
    role: Yup.string().required("Role is required"),
  }),
  create: Yup.object({
    coworker_code: Yup.string()
      .required("Coworker code is required")
      .min(3, "Minimum 3 characters required")
      .max(10, "Cannot exceed 10 chars"),
    email: Yup.string()
      .email("Please enter a valid email")
      .required("Email is required")
      .max(255, "Cannot exceed 255 chars"),
    password: Yup.string()
      .required("Password is required")
      .min(8, "Password must be atleast 8 characters long"),
    first_name: Yup.string()
      .required("First name is required")
      .min(3, "Minimum 3 characters required")
      .max(255, "Cannot exceed 255 chars"),
    last_name: Yup.string().nullable().max(255, "Cannot exceed 255 chars"),
    designation: Yup.string()
      .nullable()
      .min(3, "Minimum 3 characters required")
      .max(50, "Cannot exceed 50 chars"),
    primary_phone_number: Yup.string("Please enter a valid phone number")
      .required('Primary phone number is required')
      .min(3, 'Please enter a valid phone number'),
    secondary_phone_number: Yup.string('Please enter a valid phone number')
      .nullable(),
    profile_pic: Yup.mixed().nullable(),
    role: Yup.string().required("Role is required"),
  }),
};

export function useCoworkerForm(coworkerDetails, mode = "Create", onSubmit) {
  const initialValues =
    mode === "Create"
      ? {
          coworker_code: "",
          email: "",
          password: "",
          first_name: "",
          last_name: "",
          designation: "",
          primary_phone_number: "",
          secondary_phone_number: "",
          profile_pic: "",
          role: "CS",
        }
      : coworkerDetails || {
          coworker_code: "",
          email: "",
          first_name: "",
          last_name: "",
          designation: "",
          primary_phone_number: "",
          secondary_phone_number: "",
          profile_pic: "",
          role: "CS",
        };

  const coworkerForm = useFormikWrapper({
    initialValues: initialValues,
    validationSchema:
      mode === "Create" ? validationSchema.create : validationSchema.edit,
    isInitialValid: false,
    onSubmit: () => {
      // Trim values
      const temp_coworkers_values = {
        ...coworkerForm.values,
        first_name: coworkerForm.values?.first_name.trim(),
        last_name: coworkerForm.values?.last_name.trim(),
        email: coworkerForm.values?.email.trim(),
        coworker_code: coworkerForm.values?.coworker_code.trim(),
      };
      coworkerForm.setValues(temp_coworkers_values);
      onSubmit && onSubmit(temp_coworkers_values, mode);
    },
  });

  useEffect(() => {
    resetForm();
  }, [coworkerDetails]);

  const resetForm = () => {
    if (coworkerDetails) {
      coworkerForm.resetForm({ values: coworkerDetails });
    } else {
      coworkerForm.resetForm({
        values: {
          coworker_code: "",
          email: "",
          first_name: "",
          last_name: "",
          designation: "",
          primary_phone_number: "",
          secondary_phone_number: "",
          profile_pic: "",
          role: "CS",
        },
      });
    }
  };

  return { ...coworkerForm, resetForm };
}
